import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Select, Divider, Spin, Row, Col } from 'antd';
import s from './s.module.less';
import { BusinessType, RepresentativeAndPersonalInfo, RepresentativeAndPersonalInfoType, CompanyInfo, BusinessInfo } from 'types/paymentSetup';
import pageS from '../../s.module.less';
import PersonalForm from './components/PersonalForm';
import BusinessForm from './components/BusiniessForm';
import { useBusinessInfo } from 'hooks/paymentSetup/useBusinessInfo';
import { editBusinessInfo } from 'api/paymentSetup';
import useGetStateLabel from 'hooks/useGetStateLabel';
import stripeLogo from 'assets/logo/stripeLogo.png';

const { Option } = Select;

const businessTypeOptions = [
    {
        value: BusinessType.INDIVIDUAL,
        label: 'Individual',
    },
    {
        value: BusinessType.SOLE_PROPRIETORSHIP,
        label: 'Company - Sole Proprietorship',
    },
    {
        value: BusinessType.SINGLE_MEMBER_LLC,
        label: 'Company - Single Member LLC',
    },
    {
        value: BusinessType.MULTIPLE_MEMBERS_LLC,
        label: 'Company - Multiple Members LLC',
    },
    {
        value: BusinessType.OTHERS,
        label: 'Others – Not sure about practice business type',
    },
];

const defaultPerson: RepresentativeAndPersonalInfo = {
    type: RepresentativeAndPersonalInfoType.REPRESENTATIVE,
    firstName: '',
    middleName: '',
    lastName: '',
    birthday: '',
    phoneNumber: '',
    email: '',
    state: '',
    city: '',
    address: '',
    zip: '',
    taxId: '',
};

const defaultCompanyInfo: CompanyInfo = {
    companyName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    taxId: '',
};

interface BusinessInfoStepProps {
    isEdit?: boolean;
    isReview?: boolean;
}

const SummaryItem = ({ label, value }: { label: string; value: React.ReactNode }) => {
    return (
        <div className={s.summaryItem}>
            <div className={s.summaryLabel}>{label}</div>
            <div className={s.summaryValue}>{value || '-'}</div>
        </div>
    );
};

const BusinessInfoStep = forwardRef<any, BusinessInfoStepProps>(({
    isEdit = true,
    isReview = false,
}, ref) => {
    const companyFormRef = useRef<any>(null);
    const representativeFormRef = useRef<any>(null);
    const personalFormRef = useRef<any>(null);
    const [businessType, setBusinessType] = useState<BusinessType>();
    const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(defaultCompanyInfo);
    const [representativeInfo, setRepresentativeInfo] = useState<RepresentativeAndPersonalInfo>({ ...defaultPerson });
    const [personalInfo, setPersonalInfo] = useState<RepresentativeAndPersonalInfo>({ ...defaultPerson, type: RepresentativeAndPersonalInfoType.PERSONAL });
    const [sameAsRep, setSameAsRep] = useState<boolean>(false);

    const [apiFormData, loading, fetchData] = useBusinessInfo();
    const { getStateLabel } = useGetStateLabel();

    useEffect(() => {
        const representative = apiFormData.representativeAndPersonalInfos?.find((p) => p.type === RepresentativeAndPersonalInfoType.REPRESENTATIVE) || { ...defaultPerson, type: RepresentativeAndPersonalInfoType.REPRESENTATIVE };
        const personal = apiFormData.representativeAndPersonalInfos?.find((p) => p.type === RepresentativeAndPersonalInfoType.PERSONAL) || { ...defaultPerson, type: RepresentativeAndPersonalInfoType.PERSONAL };
        if (apiFormData) {
            setBusinessType(apiFormData.businessType);
            setCompanyInfo(apiFormData.companyInfo || defaultCompanyInfo);
            setRepresentativeInfo(representative);
            setPersonalInfo(personal);
            setSameAsRep(apiFormData.sameAsRepresentative || false);
            if (apiFormData.sameAsRepresentative) {
                setPersonalInfo({ ...representative });
            }
        }
    }, [apiFormData]);

    const handleSubmit = async () => {
        try {
            if (!businessType) {
                return;
            }

            const validationPromises: Promise<any>[] = [];
            if (companyFormRef.current) {
                validationPromises.push(companyFormRef.current.validateFields());
            }
            if (representativeFormRef.current) {
                validationPromises.push(representativeFormRef.current.validateFields());
            }
            if (personalFormRef.current) {
                validationPromises.push(personalFormRef.current.validateFields());
            }
            await Promise.all(validationPromises);

            const params: BusinessInfo = { businessType };
            const { id: _, ...personalInfoParams } = personalInfo;
            const { id: __, ...representativeInfoParams } = representativeInfo;

            if (businessType !== BusinessType.OTHERS) {
                if (businessType !== BusinessType.INDIVIDUAL) {
                    params.companyInfoReq = companyInfo;
                } else {
                    params.representativeAndPersonalInfoReqList = [personalInfoParams];
                }

                if ([BusinessType.SOLE_PROPRIETORSHIP, BusinessType.SINGLE_MEMBER_LLC].includes(businessType)) {
                    params.representativeAndPersonalInfoReqList = [representativeInfoParams];
                }

                if (businessType === BusinessType.MULTIPLE_MEMBERS_LLC) {
                    params.representativeAndPersonalInfoReqList = [representativeInfoParams, personalInfoParams];
                    params.sameAsRepresentative = sameAsRep;
                }
            }
            await editBusinessInfo(params);
            await fetchData();
        } catch (error) {
            console.error('Validation failed or error during submission:', error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit: handleSubmit,
        getBusinessType: () => businessType,
    }));

    const handleBusinessTypeChange = (value: BusinessType) => {
        setBusinessType(value);
        // initData();
    };

    const handlePersonalInfoChange = (type: RepresentativeAndPersonalInfoType, changedData: Partial<RepresentativeAndPersonalInfo>) => {
        if (type === RepresentativeAndPersonalInfoType.PERSONAL) {
            setPersonalInfo({ ...personalInfo, ...changedData, type });
        } else if (type === RepresentativeAndPersonalInfoType.REPRESENTATIVE) {
            setRepresentativeInfo({ ...representativeInfo, ...changedData, type });
            if (sameAsRep) {
                setPersonalInfo({ ...representativeInfo, ...changedData, type });
            }
        }
    };

    const handleBusinessInfoChange = (changedData: Partial<CompanyInfo>) => {
        setCompanyInfo({ ...companyInfo, ...changedData });
    };

    const handleSameAsRepChange = (sameAsRepValue: boolean) => {
        setSameAsRep(sameAsRepValue);
        if (sameAsRepValue) {
            setPersonalInfo({ ...representativeInfo });
        } else {
            setPersonalInfo({ ...personalInfo, type: RepresentativeAndPersonalInfoType.PERSONAL });
        }
    };

    const renderEditForm = () => {
        const Header = (
            <>
                <Divider />
                <div className={pageS.title}>Business identity verification</div>
                <div className={pageS.desc}>Provide business and tax details required to receive payments securely.</div>
            </>
        );

        switch (businessType) {
            case BusinessType.INDIVIDUAL:
                return (
                    <>
                        {isReview ? Header : null}
                        <PersonalForm
                            ref={personalFormRef}
                            value={personalInfo}
                            onChange={(changes) => handlePersonalInfoChange(RepresentativeAndPersonalInfoType.PERSONAL, changes)}
                            title="Personal details"
                        />
                    </>
                );
            case BusinessType.SOLE_PROPRIETORSHIP:
            case BusinessType.SINGLE_MEMBER_LLC:
                return (
                    <>
                        {isReview ? Header : null}
                        <BusinessForm ref={companyFormRef} value={companyInfo} onChange={handleBusinessInfoChange} />
                        <PersonalForm
                            ref={representativeFormRef}
                            value={representativeInfo}
                            onChange={(changes) => handlePersonalInfoChange(RepresentativeAndPersonalInfoType.REPRESENTATIVE, changes)}
                            title="Representative details"
                        />
                    </>
                );

            case BusinessType.MULTIPLE_MEMBERS_LLC:
                return (
                    <>
                        {isReview ? Header : null}
                        <BusinessForm ref={companyFormRef} value={companyInfo} onChange={handleBusinessInfoChange} />
                        <PersonalForm
                            ref={representativeFormRef}
                            value={representativeInfo}
                            onChange={(changes) => handlePersonalInfoChange(RepresentativeAndPersonalInfoType.REPRESENTATIVE, changes)}
                            title="Representative details"
                        />
                        <PersonalForm
                            ref={personalFormRef}
                            isOwner
                            value={personalInfo}
                            onChange={(changes) => handlePersonalInfoChange(RepresentativeAndPersonalInfoType.PERSONAL, changes)}
                            sameAsRep={sameAsRep}
                            onSameAsRepChange={handleSameAsRepChange}
                            title="Owner details"
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const renderSummaryView = () => {
        const businessTypeLabel = businessTypeOptions.find((opt) => opt.value === businessType)?.label || businessType;
        const currentRepresentative = representativeInfo;
        const currentPersonal = personalInfo;
        const currentCompanyInfo = companyInfo;
        const showOwners = businessType === BusinessType.MULTIPLE_MEMBERS_LLC;
        const showPersonal = businessType === BusinessType.INDIVIDUAL;
        const showRepresentative = businessType !== BusinessType.INDIVIDUAL && businessType !== BusinessType.OTHERS;
        const showCompany = businessType !== BusinessType.INDIVIDUAL && businessType !== BusinessType.OTHERS;

        return (
            <div className={s.summaryContainer}>
                <Row gutter={[24, 16]}>
                    <Col span={24}>
                        <SummaryItem label="Business type" value={businessTypeLabel} />
                    </Col>
                </Row>

                {showCompany && currentCompanyInfo && (
                    <>
                        <Divider />
                        <h3 className={s.summaryTitle}>Company details</h3>
                        <Row gutter={[24, 16]}>
                            <Col span={24}><SummaryItem label="Company name" value={currentCompanyInfo.companyName} /></Col>
                            <Col span={24}><SummaryItem label="Company address" value={currentCompanyInfo.address} /></Col>
                            <Col span={8}><SummaryItem label="City" value={currentCompanyInfo.city} /></Col>
                            <Col span={8}><SummaryItem label="State" value={getStateLabel(currentCompanyInfo.state)} /></Col>
                            <Col span={8}><SummaryItem label="ZIP code" value={currentCompanyInfo.zip} /></Col>
                            <Col span={24}><SummaryItem label="Company Tax ID" value={currentCompanyInfo.taxId} /></Col>
                        </Row>
                    </>
                )}

                {showRepresentative && currentRepresentative && (
                    <>
                        <Divider />
                        <h3 className={s.summaryTitle}>Representative details</h3>
                        <Row gutter={[24, 16]}>
                            <Col span={12}><SummaryItem label="First name" value={currentRepresentative.firstName} /></Col>
                            <Col span={12}><SummaryItem label="Last name" value={currentRepresentative.lastName} /></Col>
                            <Col span={24}><SummaryItem label="Date of birth" value={currentRepresentative.birthday as string} /></Col>
                            <Col span={12}><SummaryItem label="Phone number" value={currentRepresentative.phoneNumber} /></Col>
                            <Col span={12}><SummaryItem label="Email" value={currentRepresentative.email} /></Col>
                            <Col span={24}><SummaryItem label="Address" value={currentRepresentative.address} /></Col>
                            <Col span={8}><SummaryItem label="City" value={currentRepresentative.city} /></Col>
                            <Col span={8}><SummaryItem label="State" value={getStateLabel(currentRepresentative.state)} /></Col>
                            <Col span={8}><SummaryItem label="ZIP code" value={currentRepresentative.zip} /></Col>
                            <Col span={24}><SummaryItem label="Representative Tax ID" value={currentRepresentative.taxId} /></Col>
                        </Row>
                    </>
                )}

                {(showOwners || showPersonal) && currentPersonal && (
                    <>
                        <Divider />
                        <h3 className={s.summaryTitle}>{showPersonal ? 'Personal details' : 'Owner details'}</h3>
                        <Row gutter={[24, 16]}>
                            <Col span={12}><SummaryItem label="First name" value={currentPersonal.firstName} /></Col>
                            <Col span={12}><SummaryItem label="Last name" value={currentPersonal.lastName} /></Col>
                            <Col span={24}><SummaryItem label="Date of birth" value={currentPersonal.birthday as string} /></Col>
                            <Col span={12}><SummaryItem label="Phone number" value={currentPersonal.phoneNumber} /></Col>
                            <Col span={12}><SummaryItem label="Email" value={currentPersonal.email} /></Col>
                            <Col span={24}><SummaryItem label="Address" value={currentPersonal.address} /></Col>
                            <Col span={8}><SummaryItem label="City" value={currentPersonal.city} /></Col>
                            <Col span={8}><SummaryItem label="State" value={currentPersonal.state} /></Col>
                            <Col span={8}><SummaryItem label="ZIP code" value={currentPersonal.zip} /></Col>
                            <Col span={24}><SummaryItem label="Owner's Tax ID" value={currentPersonal.taxId} /></Col>
                        </Row>
                    </>
                )}
            </div>
        );
    };

    if (!isEdit) {
        return <Spin spinning={loading}>{renderSummaryView()}</Spin>;
    }

    return (
        <Spin spinning={loading}>
            <div>
                {!isReview ? (
                    <div className={s.header}>
                        <h2 className={pageS.stepTitle}>Business information</h2>
                        <p className={pageS.desc}>
                            Provide your business type and tax info to enable secure payment processing.
                        </p>
                        <img className={s.stripeLogo} src={stripeLogo} alt="stripe logo" />
                    </div>
                ) : null}

                <div className={s.businessFormContainer}>
                    {!isReview ? <h3 className={pageS.title}>Practice business type</h3> : null}
                    {!isReview ? (
                        <p className={pageS.desc}>
                            Choose the legal entity type that applies to your practice.
                        </p>
                    ) : null}
                    <div className={pageS.card} style={{ marginTop: '16px' }}>
                        <Select
                            placeholder="Select a business type for your practice"
                            onChange={handleBusinessTypeChange}
                            value={businessType}
                            style={{ width: '100%' }}
                            className={s.businessTypeSelect}
                        >
                            {businessTypeOptions.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                        {businessType === BusinessType.OTHERS && (
                            <div className="mt-6">
                                <div className={pageS.desc}>Need help selecting a practice business type?</div>
                                <div>
                                    Our support team is happy to help you figure it out. Please email us at <a>support@kiwihealth.com</a> with a brief description of the practice&apos;s business setup.
                                </div>
                            </div>
                        )}
                    </div>
                    {renderEditForm()}
                </div>
            </div>
        </Spin>
    );
});

export default BusinessInfoStep;
